import React, { useState } from 'react';
import { Box, Card, Typography, Tabs, Tab, Stack, Avatar, Divider, Button, Chip, CircularProgress } from '@mui/material';
import { generateImageForChannel } from '../../../../services/contentApiFn';
import { handleOpenSnackBar } from '../../../../utils';

// --- Sub-components for Previews ---

export const FacebookPreview = ({ content, lang }) => {
  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', p: 2, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <Avatar src="https://via.placeholder.com/40" />
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">Your Brand Page</Typography>
          <Typography variant="caption" color="textSecondary">Just now • 🌍</Typography>
        </Box>
      </Stack>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
        {content?.text?.[lang] || 'No content available.'}
      </Typography>
      
      <Box sx={{ width: '100%', mb: 2, position: 'relative' }}>
        {content?.imageUrl ? (
          <Box sx={{ height: 250, borderRadius: 2, overflow: 'hidden' }}>
            <img src={content.imageUrl} alt="Generated for Facebook" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        ) : (
          <Box sx={{ height: 250, bgcolor: '#f0f2f5', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography color="textSecondary">{content?.suggestedImage || 'Image Placeholder'}</Typography>
          </Box>
        )}
      </Box>

      {content?.cta?.[lang] && (
        <Button variant="contained" size="small" sx={{ bgcolor: '#1877F2', mb: 1, textTransform: 'none' }}>
          {content.cta[lang]}
        </Button>
      )}
      <Divider sx={{ my: 1 }} />
      <Stack direction="row" justifyContent="space-around">
        <Button color="inherit" size="small">👍 Like</Button>
        <Button color="inherit" size="small">💬 Comment</Button>
        <Button color="inherit" size="small">↗️ Share</Button>
      </Stack>
    </Card>
  );
};

export const InstagramPreview = ({ content, lang }) => {
  return (
    <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" p={2}>
        <Avatar src="https://via.placeholder.com/40" sx={{ width: 32, height: 32 }} />
        <Typography variant="subtitle2" fontWeight="bold">your_brand_handle</Typography>
      </Stack>
      
      <Box sx={{ width: '100%', position: 'relative' }}>
        {content?.imageUrl ? (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src={content.imageUrl} alt="Generated for Instagram" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        ) : (
          <Box sx={{ height: 400, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography color="textSecondary" sx={{ textAlign: 'center' }}>{content?.suggestedImage || 'Square Image Placeholder'}</Typography>
          </Box>
        )}
      </Box>

      <Box p={2}>
        <Stack direction="row" spacing={2} mb={1}>
          <Typography variant="h6">❤️</Typography>
          <Typography variant="h6">💬</Typography>
          <Typography variant="h6">↗️</Typography>
        </Stack>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
          <strong>your_brand_handle</strong> {content?.caption?.[lang] || 'No content available.'}
        </Typography>
        {content?.hashtags?.[lang]?.length > 0 && (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            {content.hashtags[lang].join(' ')}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export const BlogPreview = ({ content, lang }) => (
  <Card sx={{ maxWidth: 800, mx: 'auto', p: 4, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
    {content?.title?.[lang] && (
      <Typography variant="h4" fontWeight="bold" mb={1} color="#1a1a1a">
        {content.title[lang]}
      </Typography>
    )}
    {content?.smallDescription?.[lang] && (
      <Typography variant="subtitle1" color="textSecondary" mb={3} sx={{ fontStyle: 'italic' }}>
        {content.smallDescription[lang]}
      </Typography>
    )}
    <Box dangerouslySetInnerHTML={{ __html: content?.body?.[lang] || '<p>No content available.</p>' }} sx={{
      '& h1': { fontSize: '2rem', mb: 2, color: '#1a1a1a' },
      '& h2': { fontSize: '1.5rem', mt: 3, mb: 2, color: '#333' },
      '& h3': { fontSize: '1.25rem', mt: 2, mb: 1, color: '#444' },
      '& p': { fontSize: '1.1rem', lineHeight: 1.6, color: '#444', mb: 2 },
      '& ul, & ol': { fontSize: '1.1rem', pl: 3, mb: 2 },
      '& li': { mb: 1 }
    }} />
    {content?.faqs?.length > 0 && (
      <Box mt={4}>
        <Typography variant="h6" fontWeight="bold" mb={2}>Frequently Asked Questions</Typography>
        {content.faqs.map((faq, idx) => (
          <Box key={idx} mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">{faq.question?.[lang]}</Typography>
            <Typography variant="body2" color="textSecondary">{faq.answer?.[lang]}</Typography>
          </Box>
        ))}
      </Box>
    )}
  </Card>
);

export const EmailPreview = ({ content, lang }) => (
  <Card sx={{ maxWidth: 600, mx: 'auto', borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
    <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderBottom: '1px solid #ddd' }}>
      <Typography variant="body2" color="textSecondary"><strong>From:</strong> Your Brand &lt;hello@brand.com&gt;</Typography>
      <Typography variant="body2" color="textSecondary"><strong>To:</strong> Customer &lt;customer@example.com&gt;</Typography>
      <Typography variant="subtitle1" fontWeight="bold" mt={1}>Subject: {content?.subject?.[lang] || 'No subject'}</Typography>
      {content?.previewText?.[lang] && (
        <Typography variant="body2" color="textSecondary" mt={0.5} sx={{ fontStyle: 'italic' }}>
          {content.previewText[lang]}
        </Typography>
      )}
    </Box>
    <Box sx={{ p: 4, bgcolor: '#fff' }}>
      <Box dangerouslySetInnerHTML={{ __html: content?.body?.[lang] || '<p>No content available.</p>' }} sx={{
        '& h2': { fontSize: '1.4rem', mb: 2, color: '#333' },
        '& h3': { fontSize: '1.2rem', mb: 1.5, color: '#444' },
        '& p': { fontSize: '1rem', lineHeight: 1.6, color: '#444', mb: 2 },
        '& ul': { pl: 3, mb: 2 },
        '& li': { mb: 1 },
        '& a': { color: '#7922D8' }
      }} />
    </Box>
  </Card>
);

// --- Main Component ---

function ChannelPreviews({ data, onUpdateContent }) {
  const [activeTab, setActiveTab] = useState(0);
  const [lang, setLang] = useState('en');
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Support both data.channels (from API) and data.content (legacy/mock)
  const content = data?.channels || data?.content || {};
  const postId = data?._id;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRegenerateImage = async (channel) => {
    if (!postId) return;
    try {
      setIsRegenerating(true);
      const res = await generateImageForChannel(postId, { channel });
      if (res?.data?.status === 'success' && onUpdateContent) {
        onUpdateContent(res.data.data);
        handleOpenSnackBar('success', 'Image regenerated successfully!');
      }
    } catch (err) {
      console.error(err);
      handleOpenSnackBar('error', 'Failed to regenerate image');
    } finally {
      setIsRegenerating(false);
    }
  };

  const tabs = [
    { label: 'Facebook', value: 'facebook', comp: FacebookPreview },
    { label: 'Instagram', value: 'instagram', comp: InstagramPreview },
    { label: 'Blog', value: 'blog', comp: BlogPreview },
    { label: 'Email', value: 'email', comp: EmailPreview }
  ];

  const CurrentPreview = tabs[activeTab].comp;
  const currentChannel = tabs[activeTab].value;
  const currentContent = content[currentChannel];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>

        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
          {(currentChannel === 'facebook' || currentChannel === 'instagram') && postId && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRegenerateImage(currentChannel)}
              disabled={isRegenerating}
              sx={{ fontWeight: 600, textTransform: 'none', borderRadius: '20px' }}
            >
              {isRegenerating ? <CircularProgress size={16} sx={{ mr: 1 }} /> : '🔄 Regenerate Image'}
            </Button>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button
            variant={lang === 'en' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setLang('en')}
            sx={{
              borderRadius: '20px',
              minWidth: '46px',
              px: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              ...(lang === 'en' ? { bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' } } : { color: '#7922D8', borderColor: '#7922D8' })
            }}
          >
            EN
          </Button>
          <Button
            variant={lang === 'ro' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setLang('ro')}
            sx={{
              borderRadius: '20px',
              minWidth: '46px',
              px: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              ...(lang === 'ro' ? { bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' } } : { color: '#7922D8', borderColor: '#7922D8' })
            }}
          >
            RO
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ p: 4, bgcolor: '#f8f9fa', borderRadius: 3, minHeight: 400 }}>
        <CurrentPreview content={currentContent} lang={lang} />
      </Box>
    </Box>
  );
}

export default ChannelPreviews;
